/**
 * TanStack Query хуки данных коммуникаций/аналитики.
 *
 * Mock-режим (по умолчанию): агрегаты считаются на клиенте из mock-коммуникаций.
 * Реальный режим (`VITE_USE_MOCK=false`): дашборд/очереди приходят готовыми из
 * colver-эндпоинтов `/workspaces/{ws}/analytics/*`, лента — из
 * `/workspaces/{ws}/communications/feed`. Имена менеджеров резолвятся по
 * участникам воркспейса (`userSid` → displayName), план звонков — из
 * `membership.metadata.callsPlan`.
 */
import {
  hashKey,
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {useAtom, useAtomValue} from 'jotai';
import {isAxiosError} from 'axios';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {
  useActiveWorkspace,
  useWorkspaceMembers,
  usePersonalScope,
} from '@api/workspaces';
import type {WorkspaceMember} from '@api/workspaces';
import {useAuth} from '@api/auth';
import {
  fetchCommunications,
  fetchDashboard,
  fetchFeed,
  fetchManagerBrief,
  fetchMinutes,
  generateManagerRecommendation,
  fetchQueues,
  getCommunicationMediaBlob,
  getCommunicationResult,
  getCommunicationStatus,
  meerecClient,
  startReprocess,
  startReprocessScoped,
  type ReprocessOptions,
} from './client';
import type {
  AnalyticsRequest,
  CommunicationsFilter,
  CommunicationsListRequest,
  DashboardResponse,
  FeedRequest,
  FeedResponse,
  FeedRow,
  ManagementCase,
  ManagerBrief,
  MinutesRequest,
  QueueItemApi,
  QueuesResponse,
} from './types';
import {isFeedRowPending} from './types';
import {analyticsSectionFilter, dashboardBranchAtom} from './dashboard-filter';
import {
  dashKpiFromCommunications,
  groupAnalyticsFromCommunications,
  objectionsStats,
  refusalsStats,
  redFlagStats,
  callsDynamicsByDay,
  scoreDistribution,
  filterByPeriod,
  matchesAnalyticsChannel,
  urgentQueueFromCommunications,
  managementQueueFromCommunications,
  practicesQueueFromCommunications,
  type CallsDay,
  type DashKpi,
  type GroupAnalyticsRow,
  type ObjectionRow,
  type RefusalRow,
  type ScoreBucket,
  type ScoreBucketColor,
  type UrgentQueueItem,
} from './aggregations';
import {periodRange, recentDaysRange} from '@shared/lib/period';
import {periodStateAtom, analyticsChannelFilterAtom} from '@lib/atoms';
import type {Period} from '@lib/atoms';
import type {Employee} from '@entities/employee/model/types';
import {IS_MOCK} from '../mock-mode';

export const meerecKeys = {
  communications: (filter: CommunicationsFilter) =>
    ['communications', filter] as const,
  communication: (id: string) => ['communication', id] as const,
  communicationStatus: (id: string) => ['communication', id, 'status'] as const,
  communicationResult: (id: string) => ['communication', id, 'result'] as const,
  communicationMedia: (id: string) => ['communication', id, 'media'] as const,
  communicationsList: (
    workspaceSid: string | null,
    body: CommunicationsListRequest,
  ) => ['communications-list', workspaceSid, body] as const,
  queueItems: () => ['queue-items'] as const,
  kpi: (period: string) => ['kpi', period] as const,
  employees: () => ['employees'] as const,
  employee: (id: string) => ['employee', id] as const,
  feed: (workspaceSid: string | null, body: FeedRequest) =>
    ['feed', workspaceSid, body] as const,
  dashboard: (workspaceSid: string | null, range: {dateFrom: string}) =>
    ['dashboard', workspaceSid, range] as const,
  queues: (workspaceSid: string | null, range: {dateFrom: string}) =>
    ['queues', workspaceSid, range] as const,
  minutes: (workspaceSid: string | null, body: MinutesRequest) =>
    ['minutes', workspaceSid, body] as const,
  managerBrief: (workspaceSid: string | null, userSid: string) =>
    ['manager-brief', workspaceSid, userSid] as const,
};

// ─── Общие источники ────────────────────────────────────────────────────────

/** Sid активного воркспейса (null — пока не выбран/не загружен). */
const useWorkspaceSid = (): string | null => useActiveWorkspace().activeId;

/** Участники воркспейса (только real-режим). */
const useMembers = (enabled = true) => {
  const ws = useWorkspaceSid();
  const scope = usePersonalScope();
  const {currentUser} = useAuth();
  const query = useWorkspaceMembers(
    IS_MOCK || !enabled || !scope.isReady || scope.isPersonal ? null : ws,
  );
  // Для подписей своих звонков достаточно уже загруженных auth/access, без roster.
  const self = useMemo<WorkspaceMember[] | undefined>(
    () =>
      scope.isReady && scope.isPersonal && scope.userSid
        ? [
            {
              sid: scope.access?.membershipSid ?? '',
              workspaceId: ws!,
              userSid: scope.userSid,
              state: 'active',
              displayName: currentUser?.displayName,
              email: currentUser?.email,
            },
          ]
        : undefined,
    [scope.signature, currentUser?.displayName, currentUser?.email, ws],
  );
  return {...query, data: scope.isPersonal ? self : query.data};
};

/** Имя участника для UI. */
const memberName = (m: WorkspaceMember | undefined): string =>
  m?.displayName || m?.email || (m ? m.userSid.slice(0, 8) : '—');

/** Карта userSid → участник воркспейса (real-режим; в mock — пустая). */
export const useMembersMap = (): Map<string, WorkspaceMember> => {
  const {data: members = []} = useMembers();
  return useMemo(() => new Map(members.map((m) => [m.userSid, m])), [members]);
};

/**
 * Имя менеджера строки ленты («Звонки», «Встречи»): участник воркспейса по
 * `userSid`, иначе `metadata.managerName` (mock), иначе начало sid.
 */
export const useFeedManagerName = (): ((row: FeedRow) => string) => {
  const membersMap = useMembersMap();
  return useCallback(
    (row: FeedRow): string => {
      const member = row.userSid ? membersMap.get(row.userSid) : undefined;
      const metaName = row.metadata?.['managerName'];
      return (
        member?.displayName ||
        member?.email ||
        (typeof metaName === 'string' ? metaName : '') ||
        (row.userSid ? row.userSid.slice(0, 8) : '—')
      );
    },
    [membersMap],
  );
};

/** Имя менеджера по userSid (real) либо исходное имя из mock-данных. */
const resolveName = (
  map: Map<string, WorkspaceMember>,
  userSid: string | null | undefined,
  fallback?: string,
): string => {
  if (userSid && map.has(userSid)) return memberName(map.get(userSid));
  return fallback || (userSid ? userSid.slice(0, 8) : '—');
};

/** Диапазон дат текущего периода «Аналитики за период». */
const useAnalyticsRange = () => {
  const periodState = useAtomValue(periodStateAtom);
  // Зависимость — весь стейт периода: смена месяца/квартала/года (kind тот же)
  // меняет range → меняет queryKey → рефетч. Раньше зависели только от kind,
  // из-за чего month→month не перезапрашивался и данные «залипали».
  return useMemo(() => periodRange(periodState), [periodState]);
};

/**
 * Реальный дашборд-запрос (один на все секции аналитики) в срезе таба
 * «Аналитики за период» (все / звонки / очные / онлайн-встречи).
 */
const useDashboardQuery = () => {
  const ws = useWorkspaceSid();
  const scope = usePersonalScope();
  const range = useAnalyticsRange();
  const branchState = useAtomValue(dashboardBranchAtom);
  const channelFilter = useAtomValue(analyticsChannelFilterAtom);
  const director = scope.role === 'commercial-director' && !scope.isOperator;
  const branch =
    director && branchState.workspaceSid === ws ? branchState.value : 'all';
  const dashboardKey = [
    ...meerecKeys.dashboard(ws, range),
    branch,
    ...scope.queryScope,
  ];
  return useQuery({
    queryKey: [...dashboardKey, channelFilter],
    queryFn: () =>
      fetchDashboard(ws as string, {
        dateFrom: range.dateFrom,
        dateTo: range.dateTo,
        ...analyticsSectionFilter(channelFilter),
        ...(scope.isPersonal ? {userSids: [scope.userSid!]} : {}),
        ...(director ? {teamLimit: 100} : {}),
        ...(branch === 'without-branch'
          ? {withoutBranch: true}
          : branch !== 'all'
            ? {branchSid: branch}
            : {}),
      } satisfies AnalyticsRequest),
    enabled: !IS_MOCK && scope.isReady,
    staleTime: 30_000,
    // Смена таба — тот же дашборд в другом срезе: до ответа держим прежние
    // данные, а не скелетон всей страницы (он — только на первую загрузку).
    placeholderData: (previous, previousQuery) =>
      previousQuery &&
      hashKey(previousQuery.queryKey.slice(0, -1)) === hashKey(dashboardKey)
        ? previous
        : undefined,
  });
};

/** Фильтр филиала использует только варианты из авторизованного dashboard. */
export const useDashboardBranchFilter = () => {
  const ws = useWorkspaceSid();
  const scope = usePersonalScope();
  const query = useDashboardQuery();
  const [state, setState] = useAtom(dashboardBranchAtom);
  const director = scope.role === 'commercial-director' && !scope.isOperator;
  const value = director && state.workspaceSid === ws ? state.value : 'all';
  const branches = query.data?.branches ?? [];
  const hasUnassignedTeams = query.data?.hasUnassignedTeams ?? false;

  useEffect(() => {
    if (!query.data || value === 'all') return;
    if (value === 'without-branch' && hasUnassignedTeams) return;
    if (branches.some((branch) => branch.sid === value)) return;
    setState({workspaceSid: ws, value: 'all'});
  }, [query.data, value, hasUnassignedTeams, branches, setState, ws]);

  return {
    visible: director && branches.length > 0,
    value,
    branches,
    hasUnassignedTeams,
    setValue: (next: string) => setState({workspaceSid: ws, value: next}),
  };
};

/** Реальный запрос очередей дашборда. Очередь внимания отражает состояние
 *  «на данный момент» и НЕ зависит от периода «Аналитики»: используем
 *  фиксированное окно до сейчас, чтобы смена периода не перезапрашивала
 *  `/queues` (range стабилен → queryKey не меняется). */
const useQueuesQuery = () => {
  const ws = useWorkspaceSid();
  const scope = usePersonalScope();
  const branchState = useAtomValue(dashboardBranchAtom);
  const director = scope.role === 'commercial-director' && !scope.isOperator;
  const branch =
    director && branchState.workspaceSid === ws ? branchState.value : 'all';
  // Широкое окно «до текущего момента», анкор — сегодня, не periodState.
  const range = useMemo(() => recentDaysRange(365), []);
  return useQuery({
    queryKey: [...meerecKeys.queues(ws, range), branch, ...scope.queryScope],
    queryFn: () =>
      fetchQueues(ws as string, {
        dateFrom: range.dateFrom,
        dateTo: range.dateTo,
        // Бэкенд ограничивает limit очередей сотней элементов.
        limit: 100,
        ...(scope.isPersonal ? {userSids: [scope.userSid!]} : {}),
        ...(branch === 'without-branch'
          ? {withoutBranch: true}
          : branch !== 'all'
            ? {branchSid: branch}
            : {}),
      }),
    enabled: !IS_MOCK && scope.isReady,
    staleTime: 30_000,
  });
};

// ─── Базовые хуки ───────────────────────────────────────────────────────────

export const useCommunications = (filter: CommunicationsFilter = {}) => {
  const scope = usePersonalScope();
  const effective = scope.isPersonal
    ? {...filter, managerId: scope.userSid ?? undefined}
    : filter;
  return useQuery({
    queryKey: [...meerecKeys.communications(effective), ...scope.queryScope],
    queryFn: () => meerecClient.getCommunications(effective),
    enabled: scope.isReady,
  });
};

/** Почему звонок не удалось открыть. */
export type CommunicationUnavailableReason = 'gone' | 'failed';

/**
 * Предзагрузка карточки звонка перед открытием модалки.
 *
 * Нужна точкам входа «извне» (уведомления, задачи): пока пользователь дошёл до
 * ссылки, звонок мог стать недоступен (удалён или ушёл из зоны видимости).
 * Проверяем доступность ДО открытия модалки, чтобы не показывать пустую
 * карточку с ошибкой. Отметка «решено» звонок недоступным не делает.
 *
 * `gone` — бэкенд ответил 404/403 (звонка нет или он больше не виден);
 * `failed` — сеть/5xx, то есть проблема временная.
 *
 * Результат кладётся в тот же ключ кэша, что и `useCommunication`, поэтому
 * последующее открытие модалки данные уже не перезапрашивает.
 */
export const useEnsureCommunication = () => {
  const queryClient = useQueryClient();
  const scope = usePersonalScope();
  return useCallback(
    async (
      id: string,
    ): Promise<
      {ok: true} | {ok: false; reason: CommunicationUnavailableReason}
    > => {
      if (!scope.isReady) return {ok: false, reason: 'gone'};
      try {
        await queryClient.fetchQuery({
          queryKey: [...meerecKeys.communication(id), ...scope.queryScope],
          queryFn: () => meerecClient.getCommunication(id),
        });
        return {ok: true};
      } catch (error) {
        const status = isAxiosError(error) ? error.response?.status : undefined;
        return {
          ok: false,
          reason: status === 404 || status === 403 ? 'gone' : 'failed',
        };
      }
    },
    [queryClient, scope.signature],
  );
};

/** Карточка коммуникации (ядро + результат анализа + транскрипт). */
export const useCommunication = (id: string) => {
  const scope = usePersonalScope();
  const membersMap = useMembersMap();
  const q = useQuery({
    queryKey: [...meerecKeys.communication(id), ...scope.queryScope],
    queryFn: () => meerecClient.getCommunication(id),
    enabled: !!id && scope.isReady,
  });
  // Подстановка имени менеджера: приоритет — membersMap по userSid (актуальное
  // имя после переназначения владельца), фолбэк — manager из адаптера
  // (metadata.managerName может быть устаревшим после PUT /owner).
  const data = useMemo(() => {
    if (!q.data) return q.data;
    if (q.data.managerId) {
      const member = membersMap.get(q.data.managerId);
      if (member) {
        const name = member.displayName?.trim() || member.email?.trim();
        if (name) return {...q.data, manager: name};
      }
    }
    if (q.data.manager) return q.data;
    return {...q.data, manager: resolveName(membersMap, q.data.managerId)};
  }, [q.data, membersMap]);
  return {...q, data};
};

/** Детальный операционный статус коммуникации. */
export const useCommunicationStatus = (id: string | null) => {
  const ws = useWorkspaceSid();
  const scope = usePersonalScope();
  return useQuery({
    queryKey: [
      ...meerecKeys.communicationStatus(id ?? '∅'),
      ...scope.queryScope,
    ],
    queryFn: () => getCommunicationStatus(ws as string, id as string),
    enabled: !IS_MOCK && scope.isReady && Boolean(id),
  });
};

/** Результат анализа коммуникации (`formResult` + транскрипт) — для техкарточки. */
export const useCommunicationResult = (id: string | null) => {
  const ws = useWorkspaceSid();
  const scope = usePersonalScope();
  return useQuery({
    queryKey: [
      ...meerecKeys.communicationResult(id ?? '∅'),
      ...scope.queryScope,
    ],
    queryFn: () => getCommunicationResult(ws as string, id as string),
    enabled: !IS_MOCK && scope.isReady && Boolean(id),
  });
};

/**
 * Запись коммуникации как локальный object URL (для `<audio controls>` в
 * техкарточке). Тянет Blob через авторизованный клиент (S3-прокси), затем держит
 * `URL.createObjectURL` и освобождает его при смене коммуникации/размонтировании.
 * Включается только когда карточка открыта и у коммуникации есть медиа.
 */
export const useCommunicationMediaObjectUrl = (
  id: string | null,
  enabled: boolean,
) => {
  const ws = useWorkspaceSid();
  const scope = usePersonalScope();
  const q = useQuery({
    queryKey: [
      ...meerecKeys.communicationMedia(id ?? '∅'),
      ...scope.queryScope,
    ],
    queryFn: () => getCommunicationMediaBlob(ws as string, id as string),
    enabled: !IS_MOCK && scope.isReady && Boolean(id) && enabled,
    staleTime: 5 * 60_000,
  });
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!q.data) {
      setObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(q.data);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [q.data]);
  return {objectUrl, isLoading: q.isLoading, isError: q.isError};
};

/** Нетерминальные стадии обработки — пока они есть, список диагностики поллим. */
const PENDING_PROCESSING = new Set(['pending', 'ingested', 'preprocessing']);

/**
 * Сырой список коммуникаций воркспейса (страница «Все коммуникации»): все
 * принятые коммуникации в скоупе, включая несметченные и упавшие. Реальный
 * backend (диагностика приёма не имеет mock-режима). Пока есть коммуникации в
 * нетерминальной стадии — поллим каждые 10 секунд.
 */
export const useCommunicationsList = (body: CommunicationsListRequest) => {
  const ws = useWorkspaceSid();
  return useQuery({
    queryKey: meerecKeys.communicationsList(ws, body),
    queryFn: () => fetchCommunications(ws as string, body),
    enabled: !IS_MOCK && Boolean(ws),
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      const rows = query.state.data as
        | Array<{processingStatus?: string}>
        | undefined;
      return rows &&
        rows.some((r) => PENDING_PROCESSING.has(r.processingStatus ?? ''))
        ? 10_000
        : false;
    },
  });
};

/**
 * Длительность коммуникаций за выбранный месяц (виджет «Минуты» на странице
 * «Все коммуникации»). При переключении месяца query key меняется и выполняется
 * новый запрос.
 * Только администратор воркспейса / оператор — остальным бэк отвечает 403,
 * поэтому вызывать только из admin-контекста.
 */
export const useCommunicationMinutes = (body: MinutesRequest) => {
  const ws = useWorkspaceSid();
  return useQuery({
    queryKey: meerecKeys.minutes(ws, body),
    queryFn: () => fetchMinutes(ws ?? 'mock', body),
    enabled: IS_MOCK || Boolean(ws),
  });
};

/** HTTP-статус ошибки запроса: axios — из ответа, mock — из поля `status`. */
const errorStatus = (error: unknown): number | undefined => {
  if (isAxiosError(error)) return error.response?.status;
  if (typeof error === 'object' && error !== null && 'status' in error)
    return typeof error.status === 'number' ? error.status : undefined;
  return undefined;
};

/** `detail.code` ошибки 409 генерации анализа (axios или mock). */
const managerBriefErrorCode = (error: unknown): unknown => {
  if (errorStatus(error) !== 409) return undefined;
  if (isAxiosError(error)) return error.response?.data?.detail?.code;
  return typeof error === 'object' && error !== null && 'code' in error
    ? error.code
    : undefined;
};

/** 409: за 7 дней мало целевых звонков — штатное состояние, не ошибка. */
export const isManagerBriefNotEnoughData = (error: unknown): boolean =>
  managerBriefErrorCode(error) === 'manager-brief-not-enough-data';

/**
 * Блок «Нейроаналитик» карточки менеджера (spec 20): последний анализ
 * смотрящего и доступность обновления. Дешёвое чтение без LLM; от периода
 * «Аналитики» не зависит. Ошибки доступа не повторяем.
 */
export const useManagerBrief = (userSid: string) => {
  const ws = useWorkspaceSid();
  const scope = usePersonalScope();
  return useQuery({
    queryKey: [...meerecKeys.managerBrief(ws, userSid), ...scope.queryScope],
    queryFn: () => fetchManagerBrief(ws ?? 'mock', userSid),
    enabled: Boolean(userSid) && (IS_MOCK || (scope.isReady && Boolean(ws))),
    staleTime: 5 * 60_000,
    retry: (failureCount, error) =>
      ![400, 401, 403, 404].includes(errorStatus(error) ?? 0) &&
      failureCount < 2,
  });
};

/**
 * «Получить/Обновить анализ» (LLM, до ~60 с). Ответ — новое состояние блока,
 * кладём его в кэш `useManagerBrief`. Если анализ уже обновили в другой
 * вкладке (409 refresh-locked) — перечитываем состояние.
 */
export const useGenerateManagerRecommendation = (userSid: string) => {
  const ws = useWorkspaceSid();
  const scope = usePersonalScope();
  const queryClient = useQueryClient();
  const queryKey = [
    ...meerecKeys.managerBrief(ws, userSid),
    ...scope.queryScope,
  ];
  return useMutation({
    mutationFn: () => generateManagerRecommendation(ws ?? 'mock', userSid),
    onSuccess: (brief) =>
      queryClient.setQueryData<ManagerBrief>(queryKey, brief),
    onError: (error) => {
      if (managerBriefErrorCode(error) === 'manager-brief-refresh-locked')
        void queryClient.invalidateQueries({queryKey});
    },
  });
};

export const useQueueItems = () =>
  useQuery({
    queryKey: meerecKeys.queueItems(),
    queryFn: () => meerecClient.getQueueItems(),
  });

export const useKpi = (period: string) =>
  useQuery({
    queryKey: meerecKeys.kpi(period),
    queryFn: () => meerecClient.getKpi(period),
  });

/** Менеджеры: mock — генератор; real — участники воркспейса + статы дашборда. */
export const useEmployees = () => {
  const scope = usePersonalScope();
  const mockQ = useQuery({
    queryKey: meerecKeys.employees(),
    queryFn: () => meerecClient.getEmployees(),
    enabled: IS_MOCK,
  });
  const membersQ = useMembers();
  const dashQ = useDashboardQuery();

  const realData = useMemo<Employee[] | undefined>(() => {
    if (IS_MOCK) return undefined;
    const members = membersQ.data;
    if (!members) return undefined;
    const stats = new Map(
      (dashQ.data?.managers ?? []).map((m) => [m.userSid, m]),
    );
    return members
      .filter((m) => m.state !== 'suspended')
      .map((m) => {
        const s = stats.get(m.userSid);
        const callsPlanRaw = (m.metadata ?? {})['callsPlan'];
        return {
          id: m.userSid,
          name: memberName(m),
          role: 'manager' as const,
          calls: s?.calls ?? 0,
          callsPlan: typeof callsPlanRaw === 'number' ? callsPlanRaw : 0,
          score: s?.avgScore ?? 0,
          scoreDelta: 0,
          conv: s?.crGoalAction ?? 0,
          convDelta: 0,
          objectionsTotal: s?.objectionsTotal ?? 0,
          // Бэкенд отдаёт доли уже в процентах (DashboardManagerRowDTO).
          objIdent: s?.objectionsIdentified ?? 0,
          objHandled: s?.objectionsHandled ?? 0,
          scriptCompliance: s?.scriptCompliance ?? 0,
          trend: 'stable' as const,
          scoreHistory: [],
        };
      });
  }, [membersQ.data, dashQ.data]);

  if (IS_MOCK) return mockQ;
  return {...(scope.isPersonal ? dashQ : membersQ), data: realData};
};

export const useEmployee = (id: string) => {
  const mockQ = useQuery({
    queryKey: meerecKeys.employee(id),
    queryFn: () => meerecClient.getEmployee(id),
    enabled: IS_MOCK && !!id,
  });
  const employees = useEmployees();
  if (IS_MOCK) return mockQ;
  return {
    ...employees,
    data: employees.data?.find((e) => e.id === id),
  };
};

// ─── Лента коммуникаций (страница «Звонки») ────────────────────────────────

/** Параметры запроса ленты для `useFeed`. */
export interface FeedQueryParams extends FeedRequest {
  filter: Record<string, unknown>;
  sort: FeedRequest['sort'];
  skip: number;
  limit: number;
}

/**
 * Лента коммуникаций с серверной пагинацией. Пока в выдаче есть нетерминальные
 * строки (обработка/анализ в процессе) — поллим каждые 10 секунд.
 */
export const useFeed = (
  params: FeedQueryParams,
  options: {
    enabled?: boolean;
    /** Держать прошлую страницу, пока грузится новая: грид не размонтируется
        при листании/сортировке и не теряет своё состояние. */
    keepPrevious?: boolean;
  } = {},
) => {
  const ws = useWorkspaceSid();
  const scope = usePersonalScope();
  // Обязательный self-фильтр применяется последним, до серверной пагинации.
  const body = scope.isPersonal
    ? {
        ...params,
        filter: {
          ...params.filter,
          'metadata.userSid': {$in: [scope.userSid]},
        },
      }
    : params;
  return useQuery({
    queryKey: [...meerecKeys.feed(ws, body), ...scope.queryScope],
    queryFn: () => fetchFeed(ws ?? 'mock', body),
    enabled: scope.isReady && (options.enabled ?? true),
    placeholderData: options.keepPrevious ? keepPreviousData : undefined,
    refetchInterval: (query) => {
      const data = query.state.data as FeedResponse | undefined;
      return data && data.rows.some(isFeedRowPending) ? 10_000 : false;
    },
  });
};

/** Перезапуск обработки коммуникации (+ инвалидация ленты/карточки). */
export const useReprocess = () => {
  const ws = useWorkspaceSid();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (communicationId: string) =>
      startReprocess(ws as string, communicationId),
    onSuccess: (_d, communicationId) => {
      void queryClient.invalidateQueries({queryKey: ['feed']});
      void queryClient.invalidateQueries({
        queryKey: meerecKeys.communication(communicationId),
      });
    },
  });
};

/**
 * Перезапуск обработки с выбором scope (упавшие планы / полный с транскрибацией)
 * для страницы диагностики. Инвалидирует список диагностики и статус/результат
 * конкретной коммуникации.
 */
export const useReprocessScoped = () => {
  const ws = useWorkspaceSid();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: {communicationId: string; options?: ReprocessOptions}) =>
      startReprocessScoped(ws as string, vars.communicationId, vars.options),
    onSuccess: (_d, vars) => {
      void queryClient.invalidateQueries({queryKey: ['communications-list']});
      void queryClient.invalidateQueries({
        queryKey: meerecKeys.communicationStatus(vars.communicationId),
      });
      void queryClient.invalidateQueries({
        queryKey: meerecKeys.communicationResult(vars.communicationId),
      });
    },
  });
};

// ─── Дашбордовые хуки ──────────────────────────────────────────────────────

/** Mock-источник: все агрегаты дашборда строятся из одного списка коммуникаций. */
export const useDashCommunications = () => {
  const scope = usePersonalScope();
  const filter = scope.isPersonal
    ? {managerId: scope.userSid ?? undefined}
    : {};
  return useQuery({
    queryKey: [...meerecKeys.communications(filter), ...scope.queryScope],
    queryFn: () => meerecClient.getCommunications(filter),
    enabled: IS_MOCK,
  });
};

/**
 * Статус загрузки дашборда для скелетона страницы: mock — общий список
 * коммуникаций, real — единый dashboard-запрос.
 */
export const useDashboardStatus = () => {
  const mockQ = useDashCommunications();
  const realQ = useDashboardQuery();
  return IS_MOCK ? mockQ : realQ;
};

/** Mock-срез: звонки за период «Аналитики», только целевые, с фильтром типа коммуникации. */
const useDashCommsInPeriod = () => {
  const q = useDashCommunications();
  const periodState = useAtomValue(periodStateAtom);
  const channelFilter = useAtomValue(analyticsChannelFilterAtom);
  const data = q.data
    ? filterByPeriod(q.data, periodState.kind)
        .filter((c) => c.isTargeted)
        .filter((c) => matchesAnalyticsChannel(c, channelFilter))
    : undefined;
  return {...q, data};
};

export const useDashKpi = (period: Period) => {
  const mockQ = useDashCommunications();
  const realQ = useDashboardQuery();
  const channelFilter = useAtomValue(analyticsChannelFilterAtom);
  if (IS_MOCK) {
    const data = mockQ.data
      ? dashKpiFromCommunications(
          mockQ.data
            .filter((c) => c.isTargeted)
            .filter((c) => matchesAnalyticsChannel(c, channelFilter)),
          period,
        )
      : undefined;
    return {...mockQ, data};
  }
  const kpi = realQ.data?.kpi;
  const data: DashKpi | undefined = kpi
    ? {
        callsTotal: kpi.callsTotal,
        callsTotalDelta:
          kpi.callsTotalDelta == null ? null : Math.round(kpi.callsTotalDelta),
        avgScore: kpi.avgScore,
        avgScoreDelta: kpi.avgScoreDelta,
        withResult: kpi.withResult == null ? null : Math.round(kpi.withResult),
        withResultDelta:
          kpi.withResultDelta == null ? null : Math.round(kpi.withResultDelta),
        conversion: kpi.conversion == null ? null : Math.round(kpi.conversion),
        conversionDelta:
          kpi.conversionDelta == null ? null : Math.round(kpi.conversionDelta),
        withAgreement:
          kpi.withAgreement == null ? null : Math.round(kpi.withAgreement),
        withAgreementDelta:
          kpi.withAgreementDelta == null
            ? null
            : Math.round(kpi.withAgreementDelta),
      }
    : undefined;
  return {...realQ, data};
};

export const useGroupAnalytics = () => {
  const mockQ = useDashCommsInPeriod();
  const realQ = useDashboardQuery();
  const membersMap = useMembersMap();
  const scope = usePersonalScope();
  if (IS_MOCK) {
    return {
      ...mockQ,
      data: mockQ.data
        ? groupAnalyticsFromCommunications(mockQ.data)
        : undefined,
    };
  }
  const director = scope.role === 'commercial-director' && !scope.isOperator;
  const branchNames = new Map(
    realQ.data?.branches.map((branch) => [branch.sid, branch.name]) ?? [],
  );
  const sourceRows = director ? realQ.data?.teams : realQ.data?.managers;
  const data: GroupAnalyticsRow[] | undefined = sourceRows?.map((row) => {
    const leadSid =
      director && 'leadUserSid' in row && typeof row.leadUserSid === 'string'
        ? row.leadUserSid
        : row.userSid;
    const member = membersMap.get(leadSid);
    const callsPlanRaw = (member?.metadata ?? {})['callsPlan'];
    const teamSid =
      director && 'teamSid' in row && typeof row.teamSid === 'string'
        ? row.teamSid
        : undefined;
    const branchSid =
      director && 'branchSid' in row && typeof row.branchSid === 'string'
        ? row.branchSid
        : null;
    return {
      managerId: leadSid,
      teamSid,
      branchName: branchSid ? branchNames.get(branchSid) : undefined,
      full: resolveName(membersMap, leadSid),
      calls: row.targeted,
      callsPlan: director
        ? 0
        : typeof callsPlanRaw === 'number'
          ? callsPlanRaw
          : 0,
      targeted: row.targeted,
      successful: row.successful,
      crGoalAction: Math.round(row.crGoalAction ?? 0),
      avgScore: row.avgScore,
      objectionsTotal: row.objectionsTotal,
      objectionsHandled: row.objectionsHandled,
      script: Math.round(row.scriptCompliance),
    };
  });
  return {...realQ, data};
};

export const useObjectionsStats = () => {
  const mockQ = useDashCommsInPeriod();
  const realQ = useDashboardQuery();
  if (IS_MOCK) {
    return {
      ...mockQ,
      data: mockQ.data ? objectionsStats(mockQ.data) : undefined,
    };
  }
  const data = realQ.data ? objectionsFromDashboard(realQ.data) : undefined;
  return {...realQ, data};
};

const objectionsFromDashboard = (
  d: DashboardResponse,
): {total: number; unhandledTotal: number; rows: ObjectionRow[]} => {
  const rows: ObjectionRow[] = d.objections.map((o) => ({
    name: o.name,
    count: o.count,
    share: o.share,
    handled: Math.round(o.handled),
  }));
  const total = rows.reduce((s, r) => s + r.count, 0);
  const unhandledTotal = rows.reduce(
    (s, r) => s + Math.round((r.count * (100 - r.handled)) / 100),
    0,
  );
  return {total, unhandledTotal, rows};
};

export const useRefusalsStats = () => {
  const mockQ = useDashCommsInPeriod();
  const realQ = useDashboardQuery();
  if (IS_MOCK) {
    return {...mockQ, data: mockQ.data ? refusalsStats(mockQ.data) : undefined};
  }
  const data = realQ.data
    ? {
        total: realQ.data.refusals.reduce((s, r) => s + r.count, 0),
        targetedTotal: realQ.data.kpi.callsTotal,
        rows: realQ.data.refusals as RefusalRow[],
      }
    : undefined;
  return {...realQ, data};
};

/** Фасет «Причины ред-флагов» (ТЗ 3.5): mock — локальный подсчёт, real — фасет бэка. */
export const useRedFlagStats = () => {
  const mockQ = useDashCommsInPeriod();
  const realQ = useDashboardQuery();
  if (IS_MOCK) {
    return {...mockQ, data: mockQ.data ? redFlagStats(mockQ.data) : undefined};
  }
  const rows = realQ.data?.redFlagReasons ?? [];
  const data = realQ.data
    ? {
        total: rows.reduce((s, r) => s + r.count, 0),
        rows,
      }
    : undefined;
  return {...realQ, data};
};

const WEEKDAY_LABELS = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

export const useCallsDynamics = () => {
  const mockQ = useDashCommunications();
  const realQ = useDashboardQuery();
  if (IS_MOCK) {
    const data = mockQ.data
      ? callsDynamicsByDay(mockQ.data.filter((c) => c.isTargeted))
      : undefined;
    return {...mockQ, data};
  }
  const data: CallsDay[] | undefined = realQ.data?.callsDynamics.map((d) => ({
    label: WEEKDAY_LABELS[new Date(d.date).getDay()] ?? d.date,
    date: d.date,
    value: d.count,
  }));
  return {...realQ, data};
};

const BUCKET_COLORS: ScoreBucketColor[] = [
  'green',
  'lime',
  'orange',
  'red-orange',
  'red',
];

export const useScoreDistribution = () => {
  const mockQ = useDashCommsInPeriod();
  const realQ = useDashboardQuery();
  if (IS_MOCK) {
    return {
      ...mockQ,
      data: mockQ.data ? scoreDistribution(mockQ.data) : undefined,
    };
  }
  const data: ScoreBucket[] | undefined = realQ.data
    ? [...realQ.data.scoreDistribution]
        .sort((a, b) => b.fromScore - a.fromScore)
        .map((b, i) => ({
          range:
            b.fromScore <= 0
              ? `< ${b.toScore}`
              : `${b.fromScore} — ${b.toScore}`,
          count: b.count,
          color: BUCKET_COLORS[Math.min(i, BUCKET_COLORS.length - 1)],
        }))
    : undefined;
  return {...realQ, data};
};

// ─── Очереди дашборда ──────────────────────────────────────────────────────

/** Элемент очереди контракта → строка таблицы очереди дашборда (шкала /10). */
const queueItemToUrgent = (
  item: QueueItemApi,
  membersMap: Map<string, WorkspaceMember>,
): UrgentQueueItem => {
  const name = resolveName(membersMap, item.userSid);
  const ageMinutes = Math.max(
    0,
    Math.round((Date.now() - Date.parse(item.occurredAt)) / 60000),
  );
  return {
    id: `${item.queueType}-${item.communicationId}`,
    callId: item.communicationId,
    priority: item.priority,
    isRedFlag: item.isRedFlag === true,
    problemTitle: item.problemTitle,
    problemDescription: item.problemTitle,
    managerId: item.userSid ?? '',
    managerShort: name,
    managerFull: name,
    leadShort:
      item.leadName ||
      (item.leadUserSid
        ? resolveName(membersMap, item.leadUserSid)
        : undefined),
    branchName: item.teamSid ? item.branchName || 'Без филиала' : undefined,
    clientShort: item.clientName || '—',
    score: item.score ?? 0,
    ageMinutes,
    direction: item.direction === 'inbound' ? 'incoming' : 'outgoing',
    channelType: item.channelType ?? 'phone',
    // Тип встречи — по каналу; встреча по правилу плана с другим каналом — без типа.
    meetingType:
      item.channelType !== 'meeting'
        ? null
        : item.channel === 'online-meeting'
          ? 'online'
          : item.channel === 'offline-meeting'
            ? 'offline'
            : null,
    recommendation:
      item.recommendation || 'Прослушать звонок и обсудить с менеджером.',
    context: item.problemTitle,
    tags: item.tags ?? [],
  };
};

/** Управленческий кейс → строка таблицы (без привязки к звонку). */
const managementCaseToUrgent = (
  item: ManagementCase,
  idx: number,
  membersMap: Map<string, WorkspaceMember>,
): UrgentQueueItem => {
  const name = resolveName(membersMap, item.userSid);
  return {
    id: `management-${item.userSid ?? 'team'}-${idx}`,
    callId: '',
    priority: item.priority,
    problemTitle: item.typeTitle,
    problemDescription: item.description,
    managerId: item.userSid ?? '',
    managerShort: name,
    managerFull: name,
    clientShort: '—',
    score: 0,
    ageMinutes: 0,
    direction: 'outgoing',
    channelType: 'phone' as const,
    recommendation: item.recommendation || item.description,
    context: item.description,
    tags: item.calls ? [`${item.calls} ${pluralCalls(item.calls)}`] : [],
  };
};

export const useUrgentQueue = () => {
  const mockQ = useDashCommunications();
  const realQ = useQueuesQuery();
  const membersMap = useMembersMap();
  if (IS_MOCK) {
    return {
      ...mockQ,
      data: mockQ.data ? urgentQueueFromCommunications(mockQ.data) : undefined,
    };
  }
  const data = realQ.data?.urgent.map((i) => queueItemToUrgent(i, membersMap));
  return {...realQ, data};
};

export const useManagementQueue = () => {
  const mockQ = useDashCommunications();
  const realQ = useQueuesQuery();
  const membersMap = useMembersMap();
  if (IS_MOCK) {
    return {
      ...mockQ,
      data: mockQ.data
        ? managementQueueFromCommunications(mockQ.data)
        : undefined,
    };
  }
  const data = realQ.data?.management.map((c, i) =>
    managementCaseToUrgent(c, i, membersMap),
  );
  return {...realQ, data};
};

export const usePracticesQueue = () => {
  const mockQ = useDashCommunications();
  const realQ = useQueuesQuery();
  const membersMap = useMembersMap();
  if (IS_MOCK) {
    return {
      ...mockQ,
      data: mockQ.data
        ? practicesQueueFromCommunications(mockQ.data)
        : undefined,
    };
  }
  const data = realQ.data?.practices.map((i) =>
    queueItemToUrgent(i, membersMap),
  );
  return {...realQ, data};
};

/** Сырые очереди контракта (для прокидывания queueType и т.п.). */
export const useQueues = (): ReturnType<typeof useQueuesQuery> & {
  data: QueuesResponse | undefined;
} => useQueuesQuery();

/** Сырые агрегаты дашборда (period берётся из `periodStateAtom`). */
export const useDashboard = useDashboardQuery;

// ─── Утилиты ────────────────────────────────────────────────────────────────

const pluralCalls = (n: number): string => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'звонок';
  if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100))
    return 'звонка';
  return 'звонков';
};
